import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../../../axiosInstance';

export const useOnboardingProcess = (apiKey) => {
  const pollIntervalIdRef = useRef(null);
  const onboardingErrorRef = useRef(null);
  const mainProcessPollIntervalIdRef = useRef(null);
  
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [processesList, setProcessesList] = useState(null);
  const [finishedCounts, setFinishedCounts] = useState(null);
  const [errorHelperText, setErrorHelperText] = useState('');

  const stopMonitoring = useCallback(() => {
    if (pollIntervalIdRef.current) {
      clearInterval(pollIntervalIdRef.current);
      pollIntervalIdRef.current = null;
    }
    if (mainProcessPollIntervalIdRef.current) {
      clearInterval(mainProcessPollIntervalIdRef.current);
      mainProcessPollIntervalIdRef.current = null;
    }
  }, []);

  const fetchOnboardingStatus = useCallback(
    async ({ tenantName, skipWebScrape }) => {
      try {
        if (onboardingErrorRef.current) {
          stopMonitoring();
          setOnboardingStatus(null);
          return;
        }

        setOnboardingStatus((prevState) =>
          prevState
            ? prevState
            : {
                autoGeneratingSettings: { statusImport: 'Not Started' },
                autoGeneratingPrompts: { statusImport: 'Not Started' },
                policy: { statusImport: 'Not Started' },
                downloader: { statusImport: 'Not Started' },
                converter: { statusImport: 'Not Started' },
                mapper: { statusImport: 'Not Started' },
                embedder: { statusImport: 'Not Started' },
                indexer: { statusImport: 'Not Started' },
                enrichment: { statusImport: 'Not Started' },
                aggregation: { statusImport: 'Not Started' },
                ...(!skipWebScrape
                  ? { webScraper: { statusImport: 'Not Started' } }
                  : {}),
              }
        );

        const response = await axiosInstance.get(
          `/onboardingTenant/status-onboard/${tenantName}`,
          { headers: { 't-api-key': apiKey } }
        );

        let skipConverter = false;
        if (response.data) {
          setOnboardingStatus(response.data);
          const dataFeedType =
            response.data.tenant?.DataFeedConnector?.Configurations?.dataFeed;
          if (dataFeedType === 'Shopify') {
            skipConverter = true;
          }
        }

        let steps = [
          'autoGeneratingSettings',
          'autoGeneratingPrompts',
          'policy',
          'downloader',
          'converter',
          'mapper',
          'embedder',
          'indexer',
          'enrichment',
          'aggregation',
          'webScraper',
        ];
        if (skipWebScrape) {
          steps = steps.filter((step) => step !== 'webScraper');
        }
        if (skipConverter) {
          steps = steps.filter((step) => step !== 'converter');
        }

        const finishedSteps = steps.filter(
          (step) => response.data[step]?.statusImport === 'Finished'
        );
        if (finishedSteps.length === steps.length) {
          stopMonitoring();
        }
      } catch (error) {
        console.error('Error fetching onboarding status:', error);
        if (error.message && error.message.includes('no such index')) {
          setErrorHelperText('No onboarding in progress for this tenant.');
          setOnboardingStatus(null);
          stopMonitoring();
        }
      }
    },
    [apiKey, stopMonitoring]
  );

  const fetchProcessProgress = useCallback(
    async ({ reportId, tenantId, onPoll }) => {
      try {
        const [response, countsResponse] = await Promise.all([
          axiosInstance.get(`/onboardingTenant/onboarding-progress/${reportId}`, {
            headers: { 't-api-key': apiKey },
          }),
          axiosInstance.get(`/onboardingTenant/process-logs-finished-count/${reportId}`, {
            headers: { 't-api-key': apiKey },
          }).catch(() => ({ data: null }))
        ]);
        
        if (countsResponse && countsResponse.data) {
          setFinishedCounts(countsResponse.data);
        }

        if (response.data && response.data.processes) {
          setProcessesList(response.data.processes);
          
          const isRootFinished = response.data.status === 'Finished';
          const allSubProcessesFinished = response.data.processes.every(p => 
            p.status === 'Finished' || p.status === 'Failed' || 
            p.statusImport === 'Finished' || p.statusImport === 'Failed'
          );
          
          if (isRootFinished || (allSubProcessesFinished && response.data.processes.length > 0)) {
             stopMonitoring(); 
             
             if (onPoll && typeof onPoll === "function") {
               onPoll();
               setTimeout(() => {
                 onPoll();
               }, 2000);
             }
          }
        }
      } catch (error) {
        console.error('Error fetching process progress:', error);
      }
    },
    [apiKey, stopMonitoring]
  );

  const fetchProcessProgressOnce = useCallback(
    async ({ reportId, tenantId }) => {
      try {
        const [response, countsResponse] = await Promise.all([
          axiosInstance.get(`/onboardingTenant/onboarding-progress/${reportId}`, {
            headers: { 't-api-key': apiKey },
          }),
          axiosInstance.get(`/onboardingTenant/process-logs-finished-count/${reportId}`, {
            headers: { 't-api-key': apiKey },
          }).catch(() => ({ data: null }))
        ]);
        
        if (countsResponse && countsResponse.data) {
          setFinishedCounts(countsResponse.data);
        }

        if (response.data && response.data.processes) {
          setProcessesList(response.data.processes);
        }
      } catch (error) {
        console.error('Error fetching process progress:', error);
      }
    },
    [apiKey]
  );

  const startMonitoring = useCallback(
    ({ tenantName, skipWebScrape }) => {
      stopMonitoring();
      setProcessesList(null);
      fetchOnboardingStatus({ tenantName, skipWebScrape });
      const intervalId = setInterval(() => {
        fetchOnboardingStatus({ tenantName, skipWebScrape });
      }, 3000);
      pollIntervalIdRef.current = intervalId;
    },
    [fetchOnboardingStatus, stopMonitoring]
  );

  const startMonitoringReport = useCallback(
    ({ reportId, tenantId, onPoll }) => {
      stopMonitoring();
      setOnboardingStatus(null);
      fetchProcessProgress({ reportId, tenantId, onPoll });
      const intervalId = setInterval(() => {
        fetchProcessProgress({ reportId, tenantId, onPoll });
      }, 3000);
      pollIntervalIdRef.current = intervalId;
    },
    [fetchProcessProgress, stopMonitoring]
  );

  useEffect(() => {
    return () => {
      stopMonitoring();
      if (onboardingErrorRef.current) {
        onboardingErrorRef.current = null;
      }
    };
  }, [stopMonitoring]);

  return {
    onboardingStatus,
    processesList,
    finishedCounts,
    setOnboardingStatus,
    setProcessesList,
    startMonitoring,
    startMonitoringReport,
    fetchProcessProgressOnce,
    stopMonitoring,
    errorHelperText,
    setErrorHelperText,
    onboardingErrorRef,
  };
};